import * as z from "zod";
import { code, cursorPagination, decimal, flag, pagination } from "../index";
import { soLineStatuses } from "./enums";
import { orderConfirmationDecisionDto } from "./order-confirmation";
import { salesOrderAddressDto } from "./sales-order";

// The orders as their customer reads them in Frontrow (ADR-070 point 6, ADR-219; 50-api/sales.md §2.13): a contract of
// its own, which names what the customer sees instead of hiding what it does not. Credit, the checks of the payment,
// the allocation, the stops of the process, Business Central and the internal reasons stay Tomorrow's; the statuses
// become fewer steps, named for the customer.

/**
 * Where an order stands for its customer, in the order of the stepper; `cancelled` apart.
 * - `confirmed`: confirmed, and waiting for its goods (`confirmed`, `credit_blocked`);
 * - `allocated`: goods put aside for it (`allocated`);
 * - `in_preparation`: frozen for its shipment, with its payment and documents (`locked` … `document_issued`);
 * - `shipping`: at the warehouse, or shipped in part (`released_to_3pl`, a lot at the warehouse or gone);
 * - `completed`: everything shipped, or closed.
 */
export const customerOrderStages = [
  "confirmed",
  "allocated",
  "in_preparation",
  "shipping",
  "completed",
  "cancelled",
] as const;
export type CustomerOrderStage = (typeof customerOrderStages)[number];

/**
 * Where one shipment of the order, a lot (ADR-195), stands for the customer: `awaiting_payment` waits for the payment
 * of its proforma; `at_warehouse` is being picked; `shipped` left, whatever its invoice.
 */
export const customerLotStages = [
  "in_preparation",
  "awaiting_payment",
  "at_warehouse",
  "shipped",
  "cancelled",
] as const;
export type CustomerLotStage = (typeof customerLotStages)[number];

/** The answers to the confirmation a customer filters its orders by (ADR-208): the late ones are Tomorrow's queue. */
const customerConfirmationFilters = ["awaiting", "rejected"] as const;

/** The orders of the customer of the Actor (RLS), filtered and by page. */
export const listCustomerOrdersInput = z.object({
  /** Neither completed nor cancelled: the orders in progress of the home. */
  open: flag.optional(),
  brand: z.uuid().optional(),
  /** Code of the season. */
  season: code.optional(),
  /** Where the latest revision of the order confirmation stands with the customer (ADR-208). */
  confirmation: z.enum(customerConfirmationFilters).optional(),
  /** Part of the number of the order: in Nexxt, on Joor, or the customer's own. */
  q: z.string().trim().min(1).max(60).optional(),
  sort: z.enum(["-updatedAt", "expectedDeliveryStart"]).default("-updatedAt"),
  ...pagination({ defaultSize: 20, maxSize: 50 }),
});
export type ListCustomerOrdersInput = z.input<typeof listCustomerOrdersInput>;

/** An order of the customer by id or by number. */
export const getCustomerOrderInput = z.object({ key: z.string().min(1).max(36) });
export type GetCustomerOrderInput = z.input<typeof getCustomerOrderInput>;

/** By line number. */
export const listCustomerOrderLinesInput = z.object({
  salesOrderId: z.uuid(),
  ...cursorPagination({ defaultLimit: 100, maxLimit: 500 }),
});
export type ListCustomerOrderLinesInput = z.input<typeof listCustomerOrderLinesInput>;
export const customerOrderParams = listCustomerOrderLinesInput.pick({ salesOrderId: true });
export const customerOrderLinesQuery = listCustomerOrderLinesInput.omit({ salesOrderId: true });

/** An order of the customer, in its list and on its page. */
export const customerOrderDto = z
  .object({
    id: z.uuid(),
    soNumber: z.string(),
    /** What Joor calls the order, where the customer placed it; null for an order of another channel. */
    joorOrderNumber: z.string().nullable(),
    /** The purchase order number the customer gave. */
    customerPoNumber: z.string().nullable(),
    stage: z.enum(customerOrderStages),
    /** The proforma of the order, or of one of its shipments, waits for the customer's payment. */
    awaitingPayment: z.boolean(),
    brandOrgId: z.uuid().nullable(),
    brand: z.string().nullable(),
    season: z.string().nullable(),
    currency: z.string(),
    /** Codes of the payment and delivery terms, and of the incoterm. */
    paymentTerms: z.string().nullable(),
    deliveryTerm: z.string().nullable(),
    incoterm: z.string().nullable(),
    expectedDeliveryStart: z.iso.date().nullable(),
    expectedDeliveryEnd: z.iso.date().nullable(),
    /** The addresses the order came with; null, the customer's own hold. */
    billTo: salesOrderAddressDto.nullable(),
    shipTo: salesOrderAddressDto.nullable(),
    /** Lines not cancelled, and their units: ordered, put aside for the order, shipped. */
    lineCount: z.number().int(),
    qtyOrdered: z.number().int(),
    qtyAllocated: z.number().int(),
    qtyShipped: z.number().int(),
    /** After the discounts of the lines and of the order, in its currency, without taxes. */
    totalAmount: decimal.optional(),
    /** When Nexxt received the order. */
    orderedAt: z.iso.datetime(),
    cancelledAt: z.iso.datetime().nullable(),
    updatedAt: z.iso.datetime(),
  })
  .meta({ id: "CustomerOrder" });
export type CustomerOrder = z.output<typeof customerOrderDto>;

/** A shipment of the order as one lock froze it (ADR-195), with its units. */
export const customerOrderLotDto = z
  .object({
    lotNo: z.number().int(),
    stage: z.enum(customerLotStages),
    /** The shipment of logistics (P8.1f); null until it exists. */
    fulfillmentId: z.uuid().nullable(),
    qty: z.number().int(),
    qtyShipped: z.number().int(),
    shippedAt: z.iso.datetime().nullable(),
  })
  .meta({ id: "CustomerOrderLot" });
export type CustomerOrderLot = z.output<typeof customerOrderLotDto>;

/** An order on its page: with its shipments, by number; none before its first lock. */
export const customerOrderDetailDto = customerOrderDto
  .extend({ lots: z.array(customerOrderLotDto) })
  .meta({ id: "CustomerOrderDetail" });
export type CustomerOrderDetail = z.output<typeof customerOrderDetailDto>;

/** A line of an order with what its SKU is, as the catalog said when it was copied on the line (ADR-219). */
export const customerOrderLineDto = z
  .object({
    id: z.uuid(),
    lineNo: z.number().int(),
    skuCode: z.string().nullable(),
    ean: z.string().nullable(),
    styleCode: z.string().nullable(),
    styleName: z.string().nullable(),
    colour: z.string().nullable(),
    size: z.string().nullable(),
    qtyOrdered: z.number().int(),
    qtyAllocated: z.number().int(),
    qtyShipped: z.number().int(),
    /** Wholesale price of one unit before discounts, in the currency of the order. */
    unitPrice: decimal.optional(),
    discountPct: decimal.nullable().optional(),
    status: z.enum(soLineStatuses),
    /**
     * ADR-323: the ISO week (`2027-W12`) the units still to come are expected at the warehouse, from the PO raised for
     * them once it is confirmed: an estimate, never a day. Absent for a line served from stock, or with nothing to come.
     */
    expectedWeek: z
      .string()
      .regex(/^\d{4}-W\d{2}$/)
      .optional(),
  })
  .meta({ id: "CustomerOrderLine" });
export type CustomerOrderLine = z.output<typeof customerOrderLineDto>;

/**
 * A revision of the order confirmation as its customer reads it (B-47, ADR-208, ADR-221): only the ones issued, the
 * latest first, each with its PDF and the customer's answer. Never the run that issued it, nor why one failed.
 */
export const customerOrderConfirmationDto = z
  .object({
    id: z.uuid(),
    salesOrderId: z.uuid(),
    /** 1 the first, then one more for each one issued. */
    version: z.number().int(),
    /** The PDF, a document of the order (`GET /documents/documents/{documentId}/pdf`). */
    documentId: z.uuid().nullable(),
    issuedAt: z.iso.datetime(),
    /** When a later revision replaced it: it is answered no more. */
    supersededAt: z.iso.datetime().nullable(),
    /** Issued asking the customer for an answer; otherwise it counts as accepted, as in F1 (ADR-198 point 7). */
    asksAcceptance: z.boolean(),
    /**
     * The customer can answer it now: the latest revision, which asks for an answer and has none, of an order neither
     * completed nor cancelled.
     */
    awaitsAnswer: z.boolean(),
    /** The answer, with the colleague who gave it; null until one is given. */
    decision: orderConfirmationDecisionDto.nullable(),
  })
  .meta({ id: "CustomerOrderConfirmation" });
export type CustomerOrderConfirmation = z.output<typeof customerOrderConfirmationDto>;
